import React from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Chip,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { InvestigationAlignmentViewModel } from '../../domain/reportContext/investigationAlignment';

type Props = {
  alignment: InvestigationAlignmentViewModel | null;
  title?: string;
  compact?: boolean;
  testId?: string;
};

function renderList(items: string[], limit: number): string {
  const trimmed = items.filter(Boolean).slice(0, limit);
  return trimmed.length > 0 ? trimmed.join(', ') : 'Not recorded';
}

const InvestigationAlignmentCard: React.FC<Props> = ({
  alignment,
  title = 'Investigation Alignment',
  compact = false,
  testId = 'investigation-alignment-card',
}) => {
  if (!alignment) return null;

  const detailRows = [
    { label: 'Scan template', value: alignment.scanTemplateLabel || 'Not recorded' },
    { label: 'Risk family', value: alignment.riskFamilyLabel || 'Not recorded' },
    { label: 'IIR occurrence family', value: renderList(alignment.occurrenceFamilies, 2) },
    {
      label: 'Control-review themes',
      value: renderList(
        alignment.controlReviewThemes.length > 0
          ? alignment.controlReviewThemes
          : alignment.contributingFactorThemes,
        compact ? 3 : 4,
      ),
    },
  ];

  return (
    <Paper
      variant="outlined"
      sx={{ p: compact ? 1.5 : 2, borderRadius: 3 }}
      data-testid={testId}
    >
      <Stack spacing={compact ? 1 : 1.25}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant={compact ? 'subtitle2' : 'subtitle1'} sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Tooltip title={alignment.claimBoundary || 'Claim boundary not recorded'}>
            <InfoOutlinedIcon fontSize="small" color="action" />
          </Tooltip>
          <Chip size="small" variant="outlined" label={alignment.sourceLabel || 'Curated mapping'} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {alignment.uxSummary || alignment.reportSummary || 'Claim-safe investigation mapping available.'}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 1,
            gridTemplateColumns: { xs: '1fr', md: compact ? '1fr' : 'repeat(2, minmax(0, 1fr))' },
          }}
        >
          {detailRows.map((row) => (
            <Box key={row.label}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {row.label}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.25 }}>
                {row.value}
              </Typography>
            </Box>
          ))}
        </Box>
        {alignment.causePatterns.length > 0 ? (
          <Typography variant="caption" color="text.secondary">
            Typical cause patterns: {renderList(alignment.causePatterns, compact ? 2 : 3)}
          </Typography>
        ) : null}
        <Typography variant="caption" color="text.secondary">
          Claim boundary: {alignment.claimBoundary}
        </Typography>
      </Stack>
    </Paper>
  );
};

export default InvestigationAlignmentCard;
